"use client";
import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { loginSchema } from "@/schemas/auth/auth.schema";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useTranslation } from "@/hooks/useLanguage";
import { useMutationQuery } from "@/hooks/useMutationQuery";

export default function AuthPage() {
  const { t } = useTranslation();
  const { handleApiAction, isLoading } = useMutationQuery();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  // If already logged in, redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      window.location.href = "/dashboard/overview";
    }

    // Cross-tab sync: if another tab logs in, redirect to dashboard
    const handleStorageChange = (e) => {
      if (e.key === "token" && e.newValue) {
        window.location.href = "/dashboard/overview";
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const quickUsers = [
    {
      label: t("auth.quickUsers.pmsAdmin"),
      email: "admin@pms.com",
      password: "Admin@123",
    },
    {
      label: t("auth.quickUsers.pmsStaff"),
      email: "staff@pms.com",
      password: "Staff@123",
    },
  ];

  const handleQuickFill = (user) => setFormData(user);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (isLoading("login")) return;

    try {
      // Validate with Zod
      const validated = loginSchema.parse(formData);

      const result = await handleApiAction("login", validated);
      const accessToken = result?.data?.data?.accessToken;
      const csrfToken = result?.data?.data?.csrfToken;

      if (accessToken) {
        localStorage.setItem("token", accessToken);
        if (csrfToken) localStorage.setItem("csrfToken", csrfToken);
        toast({
          type: "success",
          title: t("auth.welcomeBack"),
          message: t("auth.redirecting"),
        });
        window.location.href = "/dashboard/overview";
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const firstError = err.issues[0];
        toast({
          type: "error",
          title: t("auth.invalidInput"),
          message: firstError?.message || t("common.please_check_your_inputs"),
        });
      }
      // Other errors are handled by useMutationQuery's error toast
    }
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* Right Panel - Illustration */}
      <div className="flex w-full order-first items-center justify-center bg-muted p-8 md:order-last md:w-1/2">
        <div className="flex max-w-md flex-col items-center justify-center">
          {/* Large Hotel SVG Icon - Responsive sizing */}
          <div className="mb-6 sm:mb-8 md:mb-10">
            <svg
              className="h-48 w-48 sm:h-56 sm:w-56 md:h-64 md:w-64 lg:h-80 lg:w-80"
              viewBox="0 0 1024 1024"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M352.7 103m-32 0a32 32 0 1 0 64 0 32 32 0 1 0-64 0Z" fill="#A0D195" />
              <path d="M807.7 951h-449V192L643.8 87l163.9 105z" fill="#A0D195" />
              <path d="M643.7 951h-285V192l285-105z" fill="#A0D195" />
              <path d="M523.7 951.5h-314v-531l199-74 115 74z" fill="#A0D195" />
              <path d="M408.4 951.5H209.7V420.4l199-73.9z" fill="#A0D195" />
              <path
                d="M951.7 686.6c-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17-9.8 0-16.1 5-20.8 10.5v-24.3c3.4-3.1 5.9-6.8 8.2-9.9 5.1-7.3 7.7-10.2 12.7-10.2 5 0 7.5 3 12.7 10.2 5.1 7.2 12 17 25.7 17s20.7-9.8 25.7-17c5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 4.4 0 8-3.6 8-8s-3.6-8-8-8c-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17-9.8 0-16.1 5-20.8 10.5V192c0-2.7-1.4-5.3-3.7-6.7l-164-105.1c-1.3-0.8-2.8-1.3-4.3-1.3h-0.1c-0.9 0-1.9 0.2-2.8 0.5l-285 105.1c-3.1 1.2-5.2 4.2-5.2 7.5v167.5l-143.8 53.4c-3.1 1.2-5.2 4.2-5.2 7.5V943h-101c-4.4 0-8 3.6-8 8s3.6 8 8 8h106.2c0.9 0.3 1.8 0.5 2.8 0.5h314c1 0 1.9-0.2 2.8-0.5h398.2c4.4 0 8-3.6 8-8s-3.6-8-8-8h-109V695.5c3.4-3.1 5.9-6.8 8.1-9.9 5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 13.8 0 20.7-9.8 25.7-17 5.1-7.3 7.7-10.2 12.7-10.2 5 0 7.5 3 12.7 10.2 5.1 7.2 12 17 25.7 17 4.4 0 8-3.6 8-8s-3.6-8-8-8z m-585-489l269-99.1V943h-104V420.5c0-2.7-1.4-5.3-3.7-6.7l-115-74c-0.1-0.1-0.2-0.1-0.4-0.2-0.1-0.1-0.2-0.1-0.3-0.2-0.2-0.1-0.3-0.2-0.5-0.2-0.1 0-0.2-0.1-0.3-0.1-0.2-0.1-0.4-0.1-0.5-0.2-0.1 0-0.2-0.1-0.4-0.1s-0.4-0.1-0.5-0.1c-0.1 0-0.3 0-0.4-0.1H407.9c-0.2 0-0.3 0.1-0.5 0.1-0.1 0-0.3 0-0.4 0.1-0.1 0-0.3 0.1-0.4 0.1-0.2 0-0.3 0.1-0.5 0.1l-39.2 14.6v-156z m49.9 163.6l99 63.7V943h-99.3l0.3-581.8z m-198.9 64.9l133-49.5 16-6 34-12.6-0.3 585H217.7V426.1zM799.6 943h-148V101.5l148 94.9V943zM111.1 282.6c13.8 0 20.7-9.8 25.7-17 5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 13.8 0 20.7-9.8 25.7-17 5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 4.4 0 8-3.6 8-8s-3.6-8-8-8c-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2s-7.5-3-12.7-10.2c-5.1-7.2-12-17-25.7-17-4.4 0-8 3.6-8 8s3.6 8 8 8c5 0 7.5 3 12.7 10.2 5 7.2 11.9 17 25.7 17zM72.7 205.4c5 0 7.5 3 12.7 10.2 5.1 7.2 12 17 25.7 17s20.7-9.8 25.7-17c5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 13.8 0 20.7-9.8 25.7-17 5.1-7.3 7.7-10.2 12.7-10.2s7.5 3 12.7 10.2c5.1 7.2 12 17 25.7 17 4.4 0 8-3.6 8-8s-3.6-8-8-8c-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2-5 0-7.5-3-12.7-10.2-5.1-7.2-12-17-25.7-17s-20.7 9.8-25.7 17c-5.1 7.3-7.7 10.2-12.7 10.2s-7.5-3-12.7-10.2c-5.1-7.2-12-17-25.7-17-4.4 0-8 3.6-8 8s3.5 8 8 8zM352.7 143c22.1 0 40-17.9 40-40s-17.9-40-40-40-40 17.9-40 40 17.9 40 40 40z m0-64c13.2 0 24 10.8 24 24s-10.8 24-24 24-24-10.8-24-24 10.8-24 24-24z"
                fill="#121212"
              />
              <path
                d="M350.6 376.6l-133 49.5v139.3c20.6-29.8 27.8-74 30-88.4 5.5-35.5 35.5-26 79.5-49 31.1-16.2 43.5-39.7 46.4-59.9l-6.9 2.6-16 5.9zM366.7 353.6l12.8-4.7c11.3-18.6 15.9-43.6 59.7-58.8 48-16.7 15.5-65.8 33.5-98.5s65.3-9.8 105.3-45.8c13.7-12.3 19.9-24 22.1-34.1l-233.4 86v155.9z"
                fill="#FFFFFF"
              />
            </svg>
          </div>

          {/* Text - Responsive text sizing */}
          <div className="text-center mt-6">
            <p className="text-lg font-medium text-foreground sm:text-xl md:text-2xl whitespace-pre-line">
              {t("auth.illustrationText")}
            </p>
          </div>
        </div>
      </div>

      {/* Left Panel - Auth Form */}
      <div className="flex w-full flex-col justify-center bg-background p-6 md:w-1/2">
        <div className="mx-auto w-full max-w-md">
          <h2 className="text-3xl font-semibold text-foreground mb-4">
            {t("auth.welcome")}
          </h2>
          <form onSubmit={handleSignIn} className="space-y-5">
            <input
              name="email"
              placeholder={t("common.email")}
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-full border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
            <div className="relative">
              <input
                name="password"
                placeholder={t("common.password")}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                className="w-full rounded-full border border-border bg-background px-4 py-3 pr-12 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="text-right">
              <Link href="/forgot-password" className="text-sm text-primary hover:underline">
                {t("auth.forgotPassword")}
              </Link>
            </div>

            <Button
              type="submit"
              disabled={isLoading("login")}
              className="w-full rounded-full py-6"
            >
              {isLoading("login")
                ? t("auth.signingInButton")
                : t("common.login")}
            </Button>
          </form>

          {/* Quick Fill Buttons */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            {quickUsers.map((user) => (
              <button
                key={user.email}
                type="button"
                onClick={() => handleQuickFill(user)}
                className="rounded-lg border border-border bg-muted px-3 py-2 text-xs text-foreground hover:bg-primary hover:text-white transition-colors"
              >
                {user.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
